<template>
  <v-container
    v-if="!$apollo.loading"
    fill-height
    fluid
    profile
  >
    <v-layout wrap>
      <helper-cropper
        ref="cropper"
        @updateAvatar="updateAvatar"
      />
      <v-flex
        md6
        xs12
      >
        <v-card height="100%">
          <v-form>
            <v-flex>
              <div
                class="avatar"
                :class="me.avatar ? 'no-border': ''"
                @click.stop="$refs.image.click()"
              >
                <div v-if="!me.avatar">
                  <v-icon>mdi-cloud-upload</v-icon>
                  <p>{{ $t('projects.upload') }}</p>
                  <p>PNG / JPG</p>
                  <small>150x150px</small>
                </div>
                <img
                  v-if="me.avatar"
                  :src="me.avatar"
                  height="150"
                >
                <input
                  ref="image"
                  type="file"
                  style="display: none"
                  accept="image/*"
                  @change="setImage"
                >
              </div>
              <v-card-title>{{ $t('toolbar.myProfile') }}</v-card-title>
              <v-text-field
                id="name"
                v-model.trim="me.name"
                name="name"
                :label="$t('globals.name')"
                disabled
              />
              <v-text-field
                id="email"
                v-model.trim="me.obfuscated_email"
                name="email"
                :label="$t('globals.email')"
                disabled
              />
              <v-text-field
                v-if="me.obfuscated_phone"
                id="phone"
                v-model.trim="me.obfuscated_phone"
                name="phone"
                :label="$t('profile.phone')"
                disabled
              />
              <v-select
                v-model.trim="me.language"
                :items="lang"
                :item-text="'name'"
                :item-value="'id'"
                name="language"
                :label="$t('globals.language')"
              />
              <v-text-field
                id="telegram"
                v-model.trim="me.telegram"
                name="telegram"
                prefix="@"
                :label="$t('profile.telegram')"
                :placeholder="$t('profile.telegramPlaceholder')"
              />
              <v-text-field
                id="support-verification-code"
                v-model.trim="me.support_verification_code"
                name="support-verification-code"
                :label="$t('profile.supportCode')"
                :placeholder="$t('profile.supportCodePlaceholder')"
                :error-messages="codeError"
                :type="showCode ? 'text' : 'password'"
                @input="$v.me.support_verification_code.$touch()"
                @blur="$v.me.support_verification_code.$touch()"
              >
                <template v-slot:append>
                  <v-icon
                    style="margin-right:4px;"
                    @click="showCode = !showCode"
                  >
                    {{ showCode ? 'mdi-eye' : 'mdi-eye-off' }}
                  </v-icon>
                  <v-tooltip top>
                    <template v-slot:activator="{ on }">
                      <v-icon v-on="on">
                        mdi-information-outline
                      </v-icon>
                    </template>
                    {{ $t('profile.supportCodeHint') }}
                  </v-tooltip>
                </template>
              </v-text-field>
            </v-flex>

            <v-flex
              d-flex
              justify-end
            >
              <v-btn
                text
                outlined
                :disabled="loading"
                :loading="loading"
                @click.prevent="save"
              >
                {{ $t('profile.saveButton') }}
              </v-btn>
            </v-flex>
          </v-form>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
import UPDATE_USER_MUTATION from "@/graphql/mutations/UpdateUser.gql";
import { validationMixin } from "vuelidate";
import {
  required,
  requiredIf,
  numeric,
  minLength,
  maxLength
} from "vuelidate/lib/validators";

export default {
  name: "Profile",
  mixins: [validationMixin],
  data() {
    return {
      loading: false,
      showCode: false,
      dialog: false,
      lang: [
        { id: "en", name: this.$t("globals.english") },
        { id: "ru", name: this.$t("globals.russian") }
      ]
    };
  },
  validations() {
    return {
      me: this.rules
    };
  },
  computed: {
    rules() {
      var me = {
        support_verification_code: {
          numeric,
          minLength: minLength(6),
          maxLength: maxLength(6)
        }
      };
      return me;
    },
    codeError() {
      const errors = [];
      if (!this.$v.me.support_verification_code.$dirty) return errors;
      (!this.$v.me.support_verification_code.numeric ||
        !this.$v.me.support_verification_code.minLength ||
        !this.$v.me.support_verification_code.maxLength) &&
        errors.push(this.$t("profile.supportCodeError"));
      return errors;
    }
  },
  methods: {
    setImage(e) {
      if (!this.$refs.cropper.setImage(e)) {
        this.$refs.image.value = "";
      }
    },
    updateAvatar(avatar) {
      this.me.avatar = avatar;
    },
    save() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        this.loading = true;
        let form = {};
        form.lang = this.me.language;

        if (this.me.avatar != null) form.avatar = this.me.avatar;

        if (this.me.telegram != "" && this.me.telegram != null)
          form.telegram = this.me.telegram;

        if (
          this.me.support_verification_code != "" &&
          this.me.support_verification_code != null
        )
          form.support_verification_code = this.me.support_verification_code;

        this.$apollo
          .mutate({
            mutation: UPDATE_USER_MUTATION,
            variables: form
          })
          .then(data => {
            location.reload("true");
          })
          .catch(error => {
            this.loading = false;
            if (error.graphQLErrors[0].extensions.category == 'authorization' && error.graphQLErrors[0].message === "This action is unauthorized.") {
              this.$store.commit("app/setShowPermission");
              return;
            }
            this.errorHandler(error);
          });
      }
    }
  },
  apollo: {
    me: {
      query: require("@/graphql/queries/Me.gql")
    }
  } 
}
</script>



<style lang="scss" >
@import "@/styles/material-dashboard/_colors.scss";

.profile {
  .avatar {
    position: relative;
    left: 50%;
    top: 10px;
    transform: translate(-50%, 0);
    width: 150px;
    height: 150px;
    background: #fff;
    border: 1px solid #0a61f0;
    border-radius: 150px;
    text-align: center;
    cursor: pointer;
    overflow: hidden;
    z-index: 1;
    i {
      margin: 20px 0 5px;
      color: #9c27b0;
      font-size: 26px;
    }
    p {
      margin: 7px 0 0!important;
      &:first-of-type {
        font-weight: 700;
        color: #9c27b0;
      }
      color: rgba(0, 0, 0, 0.87);
      line-height: 0.85rem;
    }
    small {
      font-size: 11px;
    }
    &.no-border {
      border: 0 !important;
    }
  }
  .v-card__title {
    margin-bottom: 15px;
  }
  .v-card__text {
    padding: 3px;
    .v-avatar {
      border-radius: 50% !important;
      margin-top: -2px;
      .v-icon {
        color: $grey-100;
      }
    }
    span {
      font-weight: 400;
      font-size: 20px;
      margin: 10px;
      color: $black;
    }
  }
}
</style>
